import React, { useState, useRef } from 'react';

interface Reaction {
  type: string;
  user: string;
  userName?: string;
}

interface ReactionBarProps {
  reactions: Reaction[];
  currentUserId: string;
  onReact: (type: string) => void;
  compact?: boolean;
  extended?: boolean;
  /** Facebook-style: text "Mi piace" + hover popup */
  facebook?: boolean;
}

const REACTION_TYPES_STANDARD = [
  { type: 'useful', label: 'Utile', emoji: '\uD83D\uDC4D' },
  { type: 'thanks', label: 'Grazie', emoji: '\uD83D\uDE4F' },
  { type: 'interesting', label: 'Interessante', emoji: '\uD83D\uDCA1' },
  { type: 'celebrate', label: 'Celebra', emoji: '\uD83C\uDF89' },
];

const REACTION_TYPES_EXTENDED = [
  { type: 'laugh', label: 'Ahah', emoji: '\uD83D\uDE02' },
  { type: 'love', label: 'Love', emoji: '\u2764\uFE0F' },
  { type: 'fire', label: 'Fire', emoji: '\uD83D\uDD25' },
  { type: 'wow', label: 'Wow', emoji: '\uD83D\uDE2E' },
];

const EMOJI_MAP: Record<string, string> = {};
[...REACTION_TYPES_STANDARD, ...REACTION_TYPES_EXTENDED].forEach(r => { EMOJI_MAP[r.type] = r.emoji; });

const ReactionBar: React.FC<ReactionBarProps> = ({ reactions, currentUserId, onReact, compact = false, extended = false, facebook = false }) => {
  const reactionTypes = extended ? [...REACTION_TYPES_STANDARD, ...REACTION_TYPES_EXTENDED] : REACTION_TYPES_STANDARD;
  const countByType = (type: string) => reactions.filter((r) => r.type === type).length;
  const hasReacted = (type: string) => reactions.some((r) => r.type === type && r.user === currentUserId);
  const getTooltip = (rt: { type: string; label: string }) => {
    const users = reactions.filter(r => r.type === rt.type).map(r => r.userName || 'Utente');
    if (!users.length) return rt.label;
    return `${rt.label}: ${users.join(', ')}`;
  };

  // Facebook mode: "Mi piace" text button + hover popup with emojis
  if (facebook) {
    return <FacebookReactionButton
      reactions={reactions}
      currentUserId={currentUserId}
      reactionTypes={reactionTypes}
      onReact={onReact}
      countByType={countByType}
      hasReacted={hasReacted}
    />;
  }

  if (compact) {
    return (
      <div className="flex items-center gap-0.5 flex-wrap">
        {reactionTypes.map((rt) => {
          const count = countByType(rt.type);
          const active = hasReacted(rt.type);
          return (
            <button
              key={rt.type}
              onClick={() => onReact(rt.type)}
              title={getTooltip(rt)}
              className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[11px] transition-all ${
                active
                  ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                  : 'hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400'
              }`}
            >
              <span className="text-xs">{rt.emoji}</span>
              {count > 0 && <span className="font-medium">{count}</span>}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {reactionTypes.map((rt) => {
        const count = countByType(rt.type);
        const active = hasReacted(rt.type);
        return (
          <button
            key={rt.type}
            onClick={() => onReact(rt.type)}
            title={getTooltip(rt)}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
              active
                ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 ring-1 ring-indigo-300 dark:ring-indigo-700'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <span>{rt.emoji}</span>
            {count > 0 && <span>{count}</span>}
          </button>
        );
      })}
    </div>
  );
};

/** Facebook-style like button with hover emoji picker */
const FacebookReactionButton: React.FC<{
  reactions: Reaction[];
  currentUserId: string;
  reactionTypes: { type: string; label: string; emoji: string }[];
  onReact: (type: string) => void;
  countByType: (type: string) => number;
  hasReacted: (type: string) => boolean;
}> = ({ reactions, currentUserId, reactionTypes, onReact, countByType, hasReacted }) => {
  const [showPicker, setShowPicker] = useState(false);
  const hideTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  const myReaction = reactionTypes.find(rt => hasReacted(rt.type));
  const totalReactions = reactions.length;

  // Aggregate top emojis for summary
  const reactionCounts: Record<string, number> = {};
  reactions.forEach(r => { reactionCounts[r.type] = (reactionCounts[r.type] || 0) + 1; });
  const topEmojis = Object.entries(reactionCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([type]) => EMOJI_MAP[type] || '\uD83D\uDC4D');

  const handleMouseEnter = () => {
    clearTimeout(hideTimeout.current);
    setShowPicker(true);
  };
  const handleMouseLeave = () => {
    hideTimeout.current = setTimeout(() => setShowPicker(false), 300);
  };
  const handleReact = (type: string) => {
    onReact(type);
    setShowPicker(false);
  };
  const handleClick = () => {
    // Toggle default "useful" (like)
    onReact('useful');
  };

  return (
    <div className="relative inline-flex items-center" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button
        onClick={handleClick}
        className={`text-[11px] font-semibold transition-colors ${
          myReaction
            ? 'text-indigo-600 dark:text-indigo-400'
            : 'text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400'
        }`}
      >
        {myReaction ? `${myReaction.emoji} ${myReaction.label}` : 'Mi piace'}
      </button>

      {/* Reaction summary badge next to like */}
      {totalReactions > 0 && (
        <span className="inline-flex items-center gap-0.5 ml-1.5 text-[11px] text-gray-400 dark:text-gray-500">
          <span className="flex -space-x-0.5">
            {topEmojis.map((emoji, i) => (
              <span key={i} className="text-[10px] leading-none">{emoji}</span>
            ))}
          </span>
          <span className="font-medium">{totalReactions}</span>
        </span>
      )}

      {/* Emoji picker popup */}
      {showPicker && (
        <div className="absolute bottom-full left-0 mb-1.5 z-50 flex items-center gap-0.5 px-2 py-1.5 bg-white dark:bg-gray-800 rounded-full shadow-lg ring-1 ring-gray-200 dark:ring-gray-700 animate-[scaleIn_0.15s_ease-out]">
          {reactionTypes.map((rt) => {
            const active = hasReacted(rt.type);
            return (
              <button
                key={rt.type}
                onClick={() => handleReact(rt.type)}
                title={rt.label}
                className={`text-lg hover:scale-125 transition-transform duration-100 px-0.5 ${active ? 'scale-110' : ''}`}
              >
                {rt.emoji}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReactionBar;
